<script setup lang="ts">
    import { computed, onMounted, reactive, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { generateName, strCompareLowerCase } from '@common/Common'
    import WorkspaceNavigation from '@common/Components/WorkspaceNavigation.vue'

    import { DiscreteScaleEditorViewModel } from '@Scales/Components/DiscreteScaleEditor'
    import DiscreteScaleEditor from '@Scales/Components/DiscreteScaleEditor.vue'

    import type {
        DiscreteScaleReadModel,
        TchScaleTemplatesListReadModel,
        WorkspaceScalesListReadModel
    } from '@Scales/readmodels'

    import
    {
        DiscreteScaleLevelReadModel,
    } from '@Scales/readmodels'

    import
    {
        useWorkspaceScalesListQueryV1,
        useTchScaleTemplatesListQueryV1
    } from '@Scales/queries-vue-composables'

    import
    {
        useCreateWorkspaceScaleCommandV1,
        useDeleteWorkspaceScaleCommandV1,
        useUpdateWorkspaceScaleCommandV1
    } from '@Scales/commands-vue-composables'


    // ======================
    // Route
    // ======================

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))


    // ======================
    // Queries
    // ======================

    const {
        runWorkspaceScalesListQueryV1,
        modelWorkspaceScalesListQueryV1
    } = useWorkspaceScalesListQueryV1(workspaceId)

    const rm = modelWorkspaceScalesListQueryV1 as Ref<WorkspaceScalesListReadModel | null>

    const {
        runTchScaleTemplatesListQueryV1,
        modelTchScaleTemplatesListQueryV1
    } = useTchScaleTemplatesListQueryV1(workspaceId)

    const tchTemplatesModel = modelTchScaleTemplatesListQueryV1 as Ref<TchScaleTemplatesListReadModel | null>


    // ======================
    // Commands
    // ======================

    const {
        invokeCreateWorkspaceScaleCommandV1,
        onSucceedCreateWorkspaceScaleCommandV1,
        validationErrorsCreateWorkspaceScaleCommandV1
    } = useCreateWorkspaceScaleCommandV1(workspaceId)

    const {
        invokeDeleteWorkspaceScaleCommandV1,
        onSucceedDeleteWorkspaceScaleCommandV1,
        validationErrorsDeleteWorkspaceScaleCommandV1
    } = useDeleteWorkspaceScaleCommandV1(workspaceId)

    const {
        invokeUpdateWorkspaceScaleCommandV1,
        onSucceedUpdateWorkspaceScaleCommandV1,
        validationErrorsUpdateWorkspaceScaleCommandV1
    } = useUpdateWorkspaceScaleCommandV1(workspaceId)


    // ======================
    // Stable ViewModel
    // ======================

    const discreteScaleEditor = reactive(new DiscreteScaleEditorViewModel())


    // ======================
    // Derived State
    // ======================

    const workspaceName = computed(() => rm.value?.WorkspaceName ?? null)

    const scales = computed<DiscreteScaleReadModel[]>(() => rm.value?.Scales ?? [])

    const scalesView = computed<DiscreteScaleReadModel[]>(() =>
        [...scales.value].sort((a, b) => strCompareLowerCase(a.Name, b.Name))
    )

    const tchTemplates = computed<DiscreteScaleReadModel[] | null>(
        () => tchTemplatesModel.value?.Templates ?? null
    )


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Scales | TransparentChoice"
        runWorkspaceScalesListQueryV1()
        runTchScaleTemplatesListQueryV1()
    })


    // ======================
    // UI Actions
    // ======================

    function showScaleCreator(tchTemplate: DiscreteScaleReadModel | null)
    {
        if (tchTemplate)
        {
            const scale = { ...tchTemplate }

            discreteScaleEditor.showCreator(
                scale.Name,
                scale.Description,
                scale.Levels
            )
        }
        else
        {
            const name = generateName("Scale")

            discreteScaleEditor.showCreator(
                name,
                null,
                [
                    new DiscreteScaleLevelReadModel(),
                    new DiscreteScaleLevelReadModel(),
                    new DiscreteScaleLevelReadModel()
                ]
            )
        }
    }

    function showScaleEditor(model: DiscreteScaleReadModel)
    {
        discreteScaleEditor.showEditor(
            model.Id,
            model.Name,
            model.Description,
            model.Levels
        )
    }


    // ======================
    // Command Handlers
    // ======================

    function handleCreateWorkspaceScaleCommand(name: string, description: string | null, levels: string)
    {
        invokeCreateWorkspaceScaleCommandV1({
            Name: name,
            Description: description,
            LevelsSerialized: levels
        })
    }

    function handleDeleteWorkspaceScaleCommand(scaleId: number)
    {
        invokeDeleteWorkspaceScaleCommandV1({
            Id: scaleId
        })
    }

    function handleUpdateWorkspaceScaleCommand(scaleId: number, name: string, description: string | null, levels: string)
    {
        invokeUpdateWorkspaceScaleCommandV1({
            Id: scaleId,
            Name: name,
            Description: description,
            LevelsSerialized: levels
        })
    }

    // ======================
    // Success Watchers
    // ======================

    onSucceedCreateWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    onSucceedDeleteWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    onSucceedUpdateWorkspaceScaleCommandV1(() =>
    {
        runWorkspaceScalesListQueryV1()
        discreteScaleEditor.close()
    })

    // ======================
    // Failed Watchers
    // ======================

    watch(validationErrorsCreateWorkspaceScaleCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return    
        discreteScaleEditor.displayValidationErrors(validationErrors)
    })

    watch(validationErrorsUpdateWorkspaceScaleCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return    
        discreteScaleEditor.displayValidationErrors(validationErrors)
    })
</script>


<template>
    <div>
        <DiscreteScaleEditor :viewModel="discreteScaleEditor"
                             @create-scale="handleCreateWorkspaceScaleCommand"
                             @update-scale="handleUpdateWorkspaceScaleCommand"
                             @delete-scale="handleDeleteWorkspaceScaleCommand" />

        <WorkspaceNavigation :workspaceId="workspaceId" :workspaceName="workspaceName" />


        <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Scales</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">

                    <div class="dropdown">
                        <button class="btn btn-success dropdown-toggle" style="padding: 0.7em 2em;" type="button" data-toggle="dropdown">
                            Create scale
                        </button>
                        <div class="dropdown-menu" style="max-height: 300px; overflow: auto;">
                            <a class="dropdown-item my-2 dropdown-item-template" @click="showScaleCreator(null)">
                                <span>Blank</span><br /><small class="text-muted">Create scale template from scratch</small>
                            </a>
                            <div class="dropdown-divider"></div>
                            <div v-if="tchTemplates">
                                <h6 class="dropdown-header">TransparentChoice's Templates</h6>
                                <a class="dropdown-item dropdown-item-template my-2" v-for="t in tchTemplates" @click="showScaleCreator(t)">
                                    <span>{{t.Name}}</span><br /><small class="text-muted">{{t.Description}}</small>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3" v-if="scales">
                <div class="row">
                    <div class="col-sm-6 col-md-4" v-for="s in scalesView" :key="s.Id">
                        <div class="tile bg-color-gray5">
                            <div class="pointer float-right">
                                <a @click="showScaleEditor(s)">
                                    <i class="fa fa-pencil"></i>
                                </a>
                            </div>

                            <h4 class="text-ellipsis fg-color-gray" style="margin-top: 7px;">
                                <span>{{s.Name}}</span>
                            </h4>

                            <small class="text-ellipsis fg-color-gray1">
                                {{s.LevelsDescription}}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

        </div>

    </div>

</template>

<style scoped>
    .dropdown-item-template
    {
        white-space:break-spaces;
    }
</style>


